import { PermissionEnum } from '@api/enum';
import { ICameraCCTV, IClient, IProject } from '@api/models';
import useAuthPermission from '@hooks/use-auth-permission';
import { EllipsisHorizontalIcon } from '@icon/index';
import { Dropdown, DropdownItem, DropdownList, DropdownTrigger } from '@library/dropdown';
import { Tooltip } from '@library/tooltip';
import CctvStatusBadge from 'modules/cctv-cameras/components/CctvStatusBadge/CctvStatusBadge';
import useCctvStatusChecker from 'modules/cctv-cameras/hooks/useCctvStatusChecker';
import { GalleryCard, Img } from 'modules/shared/features/content';
import moment from 'moment';
import { ForwardedRef, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface ILiveCameraCard {
    cctvCamera: ICameraCCTV;
    status?: {
        isOnline: boolean;
        checked: boolean;
    };
    clientId?: string;
    projectId?: string;
}
const LiveCameraCard = forwardRef((props: ILiveCameraCard, ref: ForwardedRef<HTMLDivElement>) => {
    const {
        cctvCamera,
        //  onClick,
        // isActivator,
        status,
        clientId,
        projectId,
    } = props;
    const { hasPermission } = useAuthPermission();
    const navigate = useNavigate();

    // Use status checker with token refresh
    const { isLoading, isOffline, isOnline, status: checkerStatus } = useCctvStatusChecker({
        hlsUrl: cctvCamera?.defaultChannel?.hlsUrl,
        webrtcUrl: cctvCamera?.defaultChannel?.webrtcUrl,
        clientId,
        projectId,
        cameraId: cctvCamera?._id,
    });

    // Use checker status if available, fallback to passed status prop
    const finalStatus = {
        isOnline: isOnline,
        checked: !isLoading,
    };

    // const isOlderThan3Days = (date: string) => {
    //     return moment().diff(moment(date, 'YYYYMMDD'), 'days') <= 3;
    // };

    // const isActive = useMemo(() => {
    //     return camera.latestImage
    //         ? moment().diff(moment(camera.latestImage?.datetime, 'YYYYMMDDHHmmss'), 'hours') <= 24
    //         : false;
    // }, [camera.latestImage]);
    // const isActive = true;

    const renderImg = () => {
        if (cctvCamera.defaultChannel.latestImage || status?.isOnline)
            return (
                <Img
                    className="w-full h-full aspect-video rounded-lg object-cover object-center"
                    src={cctvCamera.defaultChannel.latestImage.url}
                    alt=""
                />
            );

        return <div className="rounded-lg aspect-video object-cover"></div>;
    };

    return (
        <div
        // onClick={onClick}
        // ref={isActivator ? ref : null}
        >
            <GalleryCard className={`${!finalStatus?.isOnline && finalStatus?.checked && 'bg-danger-100'}`}>
                <GalleryCard.ActionBar className="absolute top-0 right-0 opacity-100 visible pointer-events-auto z-50">
                    {hasPermission(PermissionEnum.LIVE_CAMERA_UPDATE) && (
                        <div>
                            <Dropdown placement="bottom-end">
                                {/* <DropdownTrigger className="w-6 h-6 grid place-items-center rounded-[4px] bg-floating-color image-overlay-button-shadow hover-transition hover:brightness-90">
                                    <EllipsisHorizontalIcon className="text-grayscale-600 bg-red-600" />
                                </DropdownTrigger> */}

                                <DropdownTrigger className="w-6 h-6 grid place-items-center rounded-[4px] bg-floating-color image-overlay-button-shadow hover-transition hover:brightness-90">
                                    <EllipsisHorizontalIcon className="text-grayscale-600" />
                                </DropdownTrigger>

                                <DropdownList className="min-w-[100px]">
                                    <DropdownItem
                                        onClick={() => {
                                            navigate(
                                                `/clients/${(cctvCamera.client as IClient)._id}/projects/${
                                                    (cctvCamera.project as IProject)._id
                                                }/cctv-cameras/${cctvCamera._id}/edit-cctv`
                                            );
                                        }}
                                    >
                                        Edit
                                    </DropdownItem>
                                    {/* {hasPermission && (
                                    <DropdownItem onClick={() => onDeleteClick(cctvCamera)} className="text-danger">
                                        Delete
                                    </DropdownItem>
                                )} */}
                                </DropdownList>
                            </Dropdown>
                        </div>
                    )}
                </GalleryCard.ActionBar>
                <GalleryCard.Media className="relative">
                    <Img
                        className="w-full aspect-image rounded-lg object-cover object-center"
                        src={cctvCamera.latestImage?.url}
                    />

                    {!finalStatus?.isOnline && finalStatus?.checked && (
                        <div className="absolute top-0 left-0 w-full h-full bg-black/[35%] rounded-lg z-[3] border-4 border-danger-500">
                            <div className="flex items-center justify-center h-full w-full text-white font-medium text-xs text-center">
                                <div>
                                    Last active
                                    <div className="font-semibold mt-[6px]">
                                        {cctvCamera.latestImage
                                            ? moment
                                                  .tz(cctvCamera.lastUpdated, cctvCamera.timezone)
                                                  .format('h:mm a [·] DD MMM, YYYY')
                                            : 'Not available'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </GalleryCard.Media>

                <GalleryCard.Title>
                    {(cctvCamera?.client as IClient)?.name ? (cctvCamera?.client as IClient)?.name : '-'}
                </GalleryCard.Title>
                <GalleryCard.Title>
                    <div>{cctvCamera.name}</div>
                </GalleryCard.Title>
                <p>{(cctvCamera?.project as IProject)?.name ? (cctvCamera?.project as IProject)?.name : '-'}</p>

                <GalleryCard.Footer>
                    <div className="flex flex-wrap gap-4 justify-between">
                        <GalleryCard.FooterItem
                            label="Camera Status"
                            content={
                                // <div className={`${statusOptions.textClass}`}>{statusOptions.label}</div>
                                !finalStatus?.checked ? (
                                    <span className="text-yellow-500 text-sm">Connecting...</span>
                                ) : finalStatus?.isOnline ? (
                                    <span className="text-green-600 text-sm">Online</span>
                                ) : (
                                    <span className="text-red-500 text-sm">Offline</span>
                                )
                            }
                        ></GalleryCard.FooterItem>

                        <GalleryCard.FooterItem
                            label="Installed On"
                            content={moment(cctvCamera?.installationDate, 'YYYYMMDD').format('DD MMM, YYYY')}
                        ></GalleryCard.FooterItem>
                    </div>
                </GalleryCard.Footer>
            </GalleryCard>
        </div>
    );
});

export default LiveCameraCard;
